<div class="flex flex-col" [ngClass]="pageMode ? '' : 'max-h-[90vh]'">
  <!-- Header (dialog only - the page has its own) -->
  @if (!pageMode) {
  <div class="flex items-start justify-between px-6 pt-5 pb-4 border-b border-neutral-100">
    <div>
      <div class="text-lg font-bold text-neutral-800">{{ isEdit ? 'Edit staff member' : 'New staff member' }}</div>
      <div class="text-sm text-neutral-500">Pick every role this person needs, then the pages they should see.</div>
    </div>
    <button class="p-1.5 rounded-full hover:bg-neutral-100 text-neutral-500" (click)="onCancel()"><mat-icon>close</mat-icon></button>
  </div>
  }

  <!-- Body (scrolls; header and buttons stay put) -->
  <div class="px-6 py-5" [ngClass]="pageMode ? 'bg-white rounded-2xl border border-gray-100 shadow-sm' : 'overflow-y-auto flex-1'">
    <div class="grid grid-cols-1 sm:grid-cols-6 gap-4">
      <div class="sm:col-span-6">
        <label class="text-xs font-medium text-neutral-500 uppercase tracking-wide">Email</label>
        <input type="email" class="mt-1 p-2.5 rounded-lg border border-neutral-300 bg-neutral-50 w-full text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-60" [(ngModel)]="email" [disabled]="isEdit" placeholder="name@nustone.co.uk" />
      </div>
      <div class="sm:col-span-3">
        <label class="text-xs font-medium text-neutral-500 uppercase tracking-wide">First name</label>
        <input type="text" class="mt-1 p-2.5 rounded-lg border border-neutral-300 bg-neutral-50 w-full text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-60" [(ngModel)]="firstname" [disabled]="lockedBySuper" />
      </div>
      <div class="sm:col-span-3">
        <label class="text-xs font-medium text-neutral-500 uppercase tracking-wide">Last name</label>
        <input type="text" class="mt-1 p-2.5 rounded-lg border border-neutral-300 bg-neutral-50 w-full text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-60" [(ngModel)]="lastname" [disabled]="lockedBySuper" />
      </div>
    </div>

    @if (isEdit) {
    <div class="mt-4 flex items-center justify-between gap-4 rounded-lg border px-4 py-3"
      [ngClass]="isActive ? 'border-emerald-200 bg-emerald-50/60' : 'border-neutral-300 bg-neutral-100'"
      [class.opacity-60]="lockedBySuper">
      <div>
        <div class="text-sm font-semibold text-neutral-800">{{ isActive ? 'Active' : 'Inactive' }}</div>
        <div class="text-xs text-neutral-500">Inactive accounts can't sign in.</div>
      </div>
      <button type="button" role="switch" [attr.aria-checked]="isActive" [disabled]="lockedBySuper"
        class="relative h-6 w-11 flex-shrink-0 rounded-full transition-colors disabled:cursor-not-allowed"
        [ngClass]="isActive ? 'bg-emerald-500' : 'bg-neutral-300'"
        (click)="isActive = !isActive">
        <span class="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform"
          [class.translate-x-5]="isActive"></span>
      </button>
    </div>
    }

    <div class="mt-5">
      <div class="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-2">Roles</div>
      @if (lockedBySuper) {
      <div class="mb-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
        This person is a super admin - only a super admin can change their access.
      </div>
      }
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        @for (r of roleOptions; track r.value) {
        <label
          class="flex items-start gap-3 border rounded-lg px-3 py-2.5 transition-colors"
          [ngClass]="[
            selected.has(r.value) ? 'border-amber-400 bg-amber-50' : 'border-neutral-200',
            isDisabled(r) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-neutral-50'
          ]">
          <input type="checkbox" class="mt-1 h-4 w-4 accent-amber-500" [checked]="selected.has(r.value)" [disabled]="isDisabled(r)" (change)="toggle(r.value)" />
          <div class="min-w-0">
            <div class="font-semibold text-neutral-800 text-sm">{{ r.label }}</div>
            <div class="text-xs text-neutral-500 leading-snug">{{ r.description }}</div>
          </div>
        </label>
        }
      </div>
    </div>

    <!-- Page access -->
    <div class="mt-6">
      <div class="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-2">Page access</div>

      @if (isSuperRole) {
      <div class="text-xs bg-neutral-100 border border-neutral-200 text-neutral-600 rounded-lg px-3 py-2.5">
        A super admin always has every page, so there is nothing to narrow.
      </div>
      } @else {
      <div class="inline-flex rounded-lg bg-neutral-100 p-1 gap-1 mb-3" [class.opacity-60]="lockedBySuper">
        <button type="button" class="px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all" [disabled]="lockedBySuper"
          [ngClass]="!customAccess ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-800'" (click)="setCustom(false)">
          Everything their roles allow
        </button>
        <button type="button" class="px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all" [disabled]="lockedBySuper"
          [ngClass]="customAccess ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-800'" (click)="setCustom(true)">
          Choose pages
        </button>
      </div>

      @if (!customAccess) {
      <p class="text-xs text-neutral-500">They see every link and page their roles give them. Switch to "Choose pages" to hide some of them.</p>
      } @else {
      <div class="rounded-xl border border-neutral-200 overflow-hidden" [class.opacity-60]="lockedBySuper">
        <div class="flex items-center justify-between gap-3 px-4 py-2.5 bg-neutral-50 border-b border-neutral-200">
          <span class="text-xs text-neutral-600"><b class="text-neutral-900">{{ chosenCount }}</b> of {{ reachable.size }} pages chosen</span>
          <span class="flex items-center gap-3 text-xs font-semibold">
            <button type="button" class="text-amber-700 hover:text-amber-800" [disabled]="lockedBySuper" (click)="selectAllPages()">Select all</button>
            <button type="button" class="text-neutral-500 hover:text-neutral-800" [disabled]="lockedBySuper" (click)="clearPages()">Clear</button>
          </span>
        </div>
        <div class="max-h-72 overflow-y-auto divide-y divide-neutral-100">
          @for (g of groups; track g.name) {
          <div class="px-4 py-3">
            <label class="flex items-center gap-2 cursor-pointer w-fit">
              <input type="checkbox" class="h-4 w-4 accent-amber-500" [checked]="groupState(g) === 'all'" [indeterminate]="groupState(g) === 'some'" [disabled]="lockedBySuper" (change)="toggleGroup(g)" />
              <span class="text-xs font-bold uppercase tracking-wide text-neutral-700">{{ g.name }}</span>
            </label>
            <div class="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 pl-6">
              @for (p of g.pages; track p.key) {
              <label class="flex items-center gap-2 cursor-pointer text-sm text-neutral-700">
                <input type="checkbox" class="h-4 w-4 accent-amber-500" [checked]="pages.has(p.key)" [disabled]="lockedBySuper" (change)="togglePage(p.key)" />
                {{ p.label }}
                @if (isExtra(p.key)) { <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100" title="Not part of their roles - only given if ticked">extra</span> }
              </label>
              }
            </div>
          </div>
          }
        </div>
        @if (pagesLocked > 0) {
        <div class="px-4 py-2 text-[11px] text-neutral-400 bg-neutral-50 border-t border-neutral-200">
          {{ pagesLocked }} more pages need other roles - add a role above to offer them here. The dashboard can be given to anyone.
        </div>
        }
      </div>
      <p class="mt-2 text-[11px] text-neutral-400">Roles decide what someone <em>can</em> reach; this list hides the rest. Changes apply within a minute of their next page load.</p>
      }
      }
    </div>

    @if (isEdit && data.member) {
    <div class="mt-6">
      <app-set-password [userId]="data.member.userid" [name]="firstname || 'this person'"></app-set-password>
    </div>
    }

    @if (!isEdit) {
    <div class="mt-4 text-xs text-neutral-500">The new staff member sets their own password with "Forgot password" on the login page.</div>
    }
  </div>

  <!-- Footer -->
  <div class="flex items-center justify-end gap-2" [ngClass]="pageMode ? 'sticky bottom-4 z-20 mt-6 px-5 py-3 rounded-2xl border border-gray-200 shadow-lg bg-white/95 backdrop-blur' : 'px-6 py-4 border-t border-neutral-100 bg-neutral-50'">
    @if (pageMode) { <span class="mr-auto text-xs" [ngClass]="hasChanges ? 'text-amber-700 font-semibold' : 'text-gray-400'">{{ hasChanges ? 'Unsaved changes' : 'No changes yet' }}</span> }
    <button class="text-xs uppercase px-5 py-2.5 rounded-lg bg-neutral-200 text-neutral-700 font-semibold hover:bg-neutral-300 transition-colors" (click)="onCancel()">{{ pageMode ? 'Back to list' : 'Cancel' }}</button>
    <button
      class="text-xs uppercase px-6 py-2.5 rounded-lg bg-amber-400 text-neutral-900 font-semibold hover:bg-amber-300 transition-colors disabled:opacity-50"
      [disabled]="saving || lockedBySuper"
      (click)="onSave()">
      {{ saving ? 'Saving...' : (isEdit ? 'Save changes' : 'Create') }}
    </button>
  </div>
</div>
